import { StaffRoleSchema, type StaffRole } from "@starsach/shared-types";
import { useSetStaffExclusion, useSetStaffRole, useStaff } from "../../api/hooks";
import { useAuth } from "../../auth/AuthProvider";
import { useStepUp } from "../../auth/StepUpProvider";
import { useVisibleEnvironments } from "../../components/Environment";
import { Hint } from "../../components/Hint";
import { Card, DataTable, EmptyState, ErrorState, LoadingState, MutationError, RelativeTime, StatusPill, inputClass, sentence } from "../../components/ui";

/**
 * Platform > Staff (ADR-0017 section 4): every Gairm staff member with
 * their staff role, and per environment the staff excluded from it. A
 * superadmin changes both; a reader sees the section and cannot; anyone
 * else does not see it. The last superadmin cannot step down, which the
 * API refuses and the screen says.
 */
export function StaffSection({ staffRole }: { staffRole: StaffRole | undefined }) {
  const { me } = useAuth();
  const q = useStaff(staffRole === "gairm-superadmin" || staffRole === "gairm-reader");
  const envs = useVisibleEnvironments();
  const setRole = useSetStaffRole();
  const setExclusion = useSetStaffExclusion();
  const { ensureFresh } = useStepUp();
  if (staffRole !== "gairm-superadmin" && staffRole !== "gairm-reader") return null;
  if (q.isPending) return <LoadingState label="Loading staff" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const { staff, exclusions, canManage } = q.data;
  const environments = envs.data?.customers ?? [];
  const guarded = (run: () => void) => void ensureFresh().then(run, () => undefined);

  return (
    <Card title={<>Staff<Hint id="access.staff-role" label="what a staff role is" /></>} className="mt-4">
      <p className="mb-3 text-sm text-neutral-600 dark:text-neutral-400">
        Every Gairm staff member may open every customer's portal, as staff (every permission) or as a reader (read only, no transcripts).
        An environment sensitive enough to need it can exclude named staff altogether.{!canManage && " Only a superadmin can change these."}
      </p>
      <MutationError of={[setRole, setExclusion]} className="mb-3" />
      <DataTable
        caption="Gairm staff and their staff role"
        rows={staff}
        rowKey={(s) => s.userId}
        sortBy={(s) => s.email ?? s.userId}
        empty={<EmptyState title="No staff yet" />}
        columns={[
          { key: "who", header: "Staff member", render: (s) => <>{s.displayName ? <>{s.displayName} <span className="text-neutral-500">({s.email ?? s.userId})</span></> : (s.email ?? s.userId)}{s.userId === me?.subject && <span className="ml-1 text-xs text-neutral-500">you</span>}</> },
          {
            key: "role",
            header: "Staff role",
            render: (s) =>
              canManage ? (
                <select
                  className={`${inputClass} mt-0 w-48`}
                  aria-label={`Staff role for ${s.email ?? s.userId}`}
                  value={s.staffRole}
                  disabled={setRole.isPending}
                  onChange={(e) => {
                    // Read before the step-up resolves: React resets a controlled select to its prop in between.
                    const staffRole = StaffRoleSchema.parse(e.target.value);
                    guarded(() => setRole.mutate({ userId: s.userId, staffRole }));
                  }}
                >
                  {StaffRoleSchema.options.map((r) => (
                    <option key={r} value={r}>{sentence(r)}</option>
                  ))}
                </select>
              ) : (
                <StatusPill status={s.staffRole} tone={s.staffRole === "gairm-reader" ? "neutral" : "ok"} />
              ),
          },
          { key: "seen", header: "Last signed in", render: (s) => (s.lastSeenAt ? <RelativeTime iso={s.lastSeenAt} /> : <StatusPill status="never" tone="warning" />) },
        ]}
      />
      <h3 className="mt-6 text-sm font-semibold">
        Excluded staff, per environment
        <Hint id="access.exclusions" label="what an exclusion does" />
      </h3>
      {envs.isError && <ErrorState error={envs.error} />}
      <DataTable
        caption="Staff excluded from each environment"
        rows={environments}
        rowKey={(e) => e.environmentId}
        sortBy={(e) => e.friendlyName}
        filter={environments.length > 5}
        empty={<EmptyState title="No environments" />}
        columns={[
          { key: "env", header: "Environment", render: (e) => e.friendlyName },
          {
            key: "excluded",
            header: "Excluded staff",
            render: (e) => {
              const excluded = exclusions[e.environmentId] ?? [];
              if (!canManage) return excluded.length === 0 ? <span className="text-sm text-neutral-500">none</span> : <span className="text-sm">{excluded.map((id) => staff.find((s) => s.userId === id)?.email ?? id).join(", ")}</span>;
              return (
                <select
                  multiple
                  className={`${inputClass} mt-0 h-24 w-64`}
                  aria-label={`Excluded staff for ${e.friendlyName}`}
                  value={excluded}
                  disabled={setExclusion.isPending}
                  onChange={(ev) => {
                    const next = new Set([...ev.target.selectedOptions].map((o) => o.value));
                    const was = new Set(excluded);
                    const changed = staff.map((s) => s.userId).find((id) => next.has(id) !== was.has(id));
                    if (changed) guarded(() => setExclusion.mutate({ environmentId: e.environmentId, userId: changed, excluded: next.has(changed) }));
                  }}
                >
                  {staff.map((s) => (
                    <option key={s.userId} value={s.userId}>{s.email ?? s.userId}</option>
                  ))}
                </select>
              );
            },
          },
        ]}
      />
    </Card>
  );
}
